import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { createColumnHelper } from "@tanstack/react-table";
import { api } from "@/lib/api";
import { usePaged } from "@/lib/hooks";
import { useEntityAction } from "@/lib/mutations";
import { PagedTable } from "@/components/PagedTable";
import { DateField } from "@/components/Form";
import { PurgeOldPanel } from "@/components/PurgeOldPanel";
import { RowActions } from "@/components/RowActions";
import { ConfirmButton } from "@/components/ConfirmButton";
import { useAuth } from "@/auth/auth";
import type { Lead } from "@/lib/types";
import { formatCompanyDateTime } from "@/lib/time";
import { tStatus } from "@/i18n";

const LEVELS = ["HOT", "WARM", "LOW"];

const col = createColumnHelper<Lead>();

/** First line / ~90 chars of the message, for the table cell. */
function excerpt(text: string): string {
  const firstLine = text.split("\n", 1)[0].trim();
  return firstLine.length > 90 ? firstLine.slice(0, 90) + "…" : firstLine;
}

export function LevelBadge({ level }: { level: string | null }) {
  if (!level) return <span className="text-sand-muted">—</span>;
  const tone =
    level === "HOT"
      ? "bg-red-500/15 text-red-700"
      : level === "WARM"
        ? "bg-gold/15 text-gold"
        : "bg-olive-600/40 text-sand-muted";
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${tone}`}>
      {tStatus(level)}
    </span>
  );
}

export default function LeadsPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const canWrite = can("leads:write");
  const [category, setCategory] = useState("");
  const [level, setLevel] = useState("");
  const [source, setSource] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const paged = usePaged<Lead>(["leads"], "/leads", {
    category: category || undefined,
    level: level || undefined,
    source: source || undefined,
    observed_from: from ? `${from}T00:00:00` : undefined,
    observed_to: to ? `${to}T23:59:59` : undefined,
    include_deleted: showDeleted ? "true" : undefined,
  });

  const action = useEntityAction([["leads"]]);
  const bulkDelete = useEntityAction([["leads"]], {
    successMessage: t("common.deleted"),
    onSuccess: () => setSelected(new Set()),
  });

  const toggleOne = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const pageIds = paged.items.map((l) => l.id);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const toggleAllOnPage = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        for (const id of pageIds) next.delete(id);
      } else {
        for (const id of pageIds) next.add(id);
      }
      return next;
    });

  const columns = useMemo(
    () => [
      ...(canWrite
        ? [
            col.display({
              id: "select",
              header: () => (
                <input
                  type="checkbox"
                  checked={allOnPageSelected}
                  onChange={toggleAllOnPage}
                  aria-label={t("leads.selectAllOnPage")}
                />
              ),
              cell: (c) => (
                <input
                  type="checkbox"
                  checked={selected.has(c.row.original.id)}
                  onChange={() => toggleOne(c.row.original.id)}
                  aria-label={t("leads.selectRow")}
                />
              ),
            }),
          ]
        : []),
      col.accessor("observed_at", {
        header: () => t("leads.observedAt"),
        cell: (c) => formatCompanyDateTime(c.getValue()),
      }),
      col.accessor("category", {
        header: () => t("leads.category"),
        cell: (c) => tStatus(c.getValue()),
      }),
      col.accessor("level", {
        header: () => t("leads.level"),
        cell: (c) => <LevelBadge level={c.getValue()} />,
      }),
      col.accessor("sender", { header: () => t("leads.sender") }),
      col.accessor("message_text", {
        header: () => t("leads.excerpt"),
        cell: (c) => (
          <Link
            className="text-sand hover:text-gold"
            to={`/leads/${c.row.original.id}`}
          >
            {excerpt(c.getValue())}
          </Link>
        ),
      }),
      col.accessor("source", {
        header: () => t("leads.source"),
        cell: (c) => tStatus(c.getValue()),
      }),
      ...(canWrite
        ? [
            col.display({
              id: "actions",
              header: () => t("common.actions"),
              cell: (c) => {
                const id = c.row.original.id;
                return (
                  <RowActions
                    deleted={!!c.row.original.deleted_at}
                    canWrite={canWrite}
                    busy={action.isPending}
                    onDelete={() => action.mutate(() => api.del(`/leads/${id}`))}
                    onRestore={() =>
                      action.mutate(() => api.post(`/leads/${id}/restore`))
                    }
                  />
                );
              },
            }),
          ]
        : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, canWrite, action, selected, allOnPageSelected, pageIds.join(",")],
  );

  const resetTo0 = () => paged.resetPage();
  const inputCls =
    "rounded-md border border-olive-600 bg-olive-800 px-2 py-1 text-sm";

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold">{t("leads.title")}</h1>
      <PagedTable
        columns={columns}
        data={paged.items}
        query={paged.query}
        total={paged.total}
        page={paged.page}
        pageCount={paged.pageCount}
        onPage={paged.setPage}
        empty={t("leads.empty")}
        toolbar={
          <div className="flex flex-wrap items-center gap-3">
            <input
              className={inputCls}
              placeholder={t("leads.category")}
              value={category}
              onChange={(e) => {
                setCategory(e.target.value);
                resetTo0();
              }}
            />
            <select
              className={inputCls}
              value={level}
              onChange={(e) => {
                setLevel(e.target.value);
                resetTo0();
              }}
            >
              <option value="">{t("leads.allLevels")}</option>
              {LEVELS.map((l) => (
                <option key={l} value={l}>
                  {tStatus(l)}
                </option>
              ))}
            </select>
            <input
              className={inputCls}
              placeholder={t("leads.source")}
              value={source}
              onChange={(e) => {
                setSource(e.target.value);
                resetTo0();
              }}
            />
            <div className="w-40">
              <DateField
                value={from}
                placeholder={t("leads.from")}
                onChange={(v) => {
                  setFrom(v);
                  resetTo0();
                }}
              />
            </div>
            <div className="w-40">
              <DateField
                value={to}
                placeholder={t("leads.to")}
                onChange={(v) => {
                  setTo(v);
                  resetTo0();
                }}
              />
            </div>
            {canWrite && (
              <label className="ml-auto flex items-center gap-1.5 text-xs text-sand-muted">
                <input
                  type="checkbox"
                  checked={showDeleted}
                  onChange={(e) => setShowDeleted(e.target.checked)}
                />
                {t("common.showDeleted")}
              </label>
            )}
          </div>
        }
      />

      {canWrite && selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-red-500/40 bg-red-500/5 p-3">
          <span className="text-sm text-sand">
            {t("leads.selectedCount", { count: selected.size })}
          </span>
          <ConfirmButton
            label={t("leads.deleteSelected")}
            confirmLabel={t("housekeeping.confirmDelete", { count: selected.size })}
            busy={bulkDelete.isPending}
            onConfirm={() =>
              bulkDelete.mutate(() =>
                api.post("/leads/delete-bulk", { lead_ids: Array.from(selected) }),
              )
            }
          />
          <button
            type="button"
            onClick={() => setSelected(new Set())}
            className="text-xs text-sand-muted hover:text-sand"
          >
            {t("leads.clearSelection")}
          </button>
        </div>
      )}

      {canWrite && (
        <PurgeOldPanel
          endpoint="/leads/purge-old"
          title={t("housekeeping.leadsTitle")}
          hint={t("housekeeping.leadsHint")}
          foundLabel={(n) => t("housekeeping.leadsFound", { count: n })}
          deletedLabel={(n) => t("housekeeping.leadsDeleted", { count: n })}
          allowDeleteAll
          deleteAllLabel={t("housekeeping.deleteAllLeads")}
          deleteAllHint={t("housekeeping.deleteAllLeadsHint")}
        />
      )}
    </div>
  );
}
